import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Checkbox,
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  MenuBook as HomeworkIcon,
  Quiz as QuizIcon,
  Assignment as ProjectIcon,
  Description as TestIcon,
  MoreHoriz as OtherIcon,
} from '@mui/icons-material';
import { useFamily } from '../../context';
import { FamilySelector, useFamilySelection, FileAttachments } from '../common';
import { updateData } from '../../services/dataApi';
import { ClassAssignment, AssignmentType } from '../../types';

const SCHOOL_TYPES = [
  { key: 'elementary', label: 'Elementary' },
  { key: 'middle', label: 'Middle' },
  { key: 'high', label: 'High School' },
  { key: 'preschool', label: 'Preschool' },
  { key: 'daycare', label: 'Daycare' },
];

// School Form Dialog
interface SchoolData {
  name: string;
  type?: string;
  principal?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  phone?: string;
  website?: string;
  notes?: string;
  memberIds?: string[];
}

interface SchoolFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: SchoolData, familyIds: string[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  school?: { id: string; data?: { data?: SchoolData } } | null;
}

export const SchoolFormDialog: React.FC<SchoolFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  school,
}) => {
  const { memberList, getMemberName, getMemberColor } = useFamily();
  const isEditing = !!school;
  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection();

  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [principal, setPrincipal] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [notes, setNotes] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (school) {
        const data = (school.data?.data || {}) as any;
        setName(data.name || '');
        setType(data.type || '');
        setPrincipal(data.principal || '');
        setEmail(data.email || '');
        setAddress(data.address || '');
        setCity(data.city || '');
        setState(data.state || '');
        setPostalCode(data.postalCode || data.postal_code || '');
        setPhone(data.phone || '');
        setWebsite(data.website || '');
        setNotes(data.notes || '');
        setMemberIds(data.memberIds || []);
      } else {
        setName('');
        setType('');
        setPrincipal('');
        setEmail('');
        setAddress('');
        setCity('');
        setState('');
        setPostalCode('');
        setPhone('');
        setWebsite('');
        setNotes('');
        setMemberIds([]);
      }
      setError(null);
    }
  }, [open, school]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('School name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        name: name.trim(),
        type: type || undefined,
        principal: principal.trim() || undefined,
        email: email.trim() || undefined,
        address: address.trim() || undefined,
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        postalCode: postalCode.trim() || undefined,
        phone: phone.trim() || undefined,
        website: website.trim() || undefined,
        notes: notes.trim() || undefined,
        memberIds: memberIds.length > 0 ? memberIds : undefined,
      }, selectedFamilyIds);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save school');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete school');
    } finally {
      setSaving(false);
    }
  };

  const toggleMember = (memberId: string) => {
    setMemberIds((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit School' : 'New School'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          {/* School Type */}
          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>School Type</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {SCHOOL_TYPES.map((st) => (
                <Chip
                  key={st.key}
                  label={st.label}
                  onClick={() => setType(type === st.key ? '' : st.key)}
                  variant={type === st.key ? 'filled' : 'outlined'}
                  color={type === st.key ? 'primary' : 'default'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Grid>

          <Grid size={12}>
            <TextField label="School Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required autoFocus />
          </Grid>

          {showFamilySelector && !isEditing && (
            <Grid size={12}>
              <FamilySelector value={selectedFamilyIds} onChange={setSelectedFamilyIds} />
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Principal" value={principal} onChange={(e) => setPrincipal(e.target.value)} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth type="email" />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth type="tel" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Website" value={website} onChange={(e) => setWebsite(e.target.value)} fullWidth type="url" />
          </Grid>

          {/* Structured Address */}
          <Grid size={12}>
            <TextField label="Street Address" value={address} onChange={(e) => setAddress(e.target.value)} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="City" value={city} onChange={(e) => setCity(e.target.value)} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="State" value={state} onChange={(e) => setState(e.target.value)} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="ZIP Code" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} fullWidth />
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>Students</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {memberList.map((member) => {
                const isSelected = memberIds.includes(member.id);
                const memberName = getMemberName(member.id);
                const color = getMemberColor(member.id);
                return (
                  <Chip
                    key={member.id}
                    avatar={<Avatar sx={{ bgcolor: color }}>{memberName.charAt(0)}</Avatar>}
                    label={memberName}
                    onClick={() => toggleMember(member.id)}
                    variant={isSelected ? 'filled' : 'outlined'}
                    color={isSelected ? 'primary' : 'default'}
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
            </Box>
          </Grid>

          <Grid size={12}>
            <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={saving} sx={{ mr: 'auto' }}>Delete</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Class Form Dialog
const SUBJECT_OPTIONS = [
  { key: 'Math', color: '#3B82F6' },
  { key: 'Science', color: '#10B981' },
  { key: 'English', color: '#F59E0B' },
  { key: 'History', color: '#6366F1' },
  { key: 'Art', color: '#EC4899' },
  { key: 'Music', color: '#EF4444' },
  { key: 'P.E.', color: '#06B6D4' },
  { key: 'Other', color: '#6B7280' },
];

const DAYS_OF_WEEK = [
  { key: 'Mon', label: 'Mon' },
  { key: 'Tue', label: 'Tue' },
  { key: 'Wed', label: 'Wed' },
  { key: 'Thu', label: 'Thu' },
  { key: 'Fri', label: 'Fri' },
];

const ASSIGNMENT_TYPES: { key: AssignmentType; label: string; icon: React.ReactElement; color: string }[] = [
  { key: 'homework', label: 'Homework', icon: <HomeworkIcon fontSize="small" />, color: '#3B82F6' },
  { key: 'test', label: 'Test', icon: <TestIcon fontSize="small" />, color: '#EF4444' },
  { key: 'quiz', label: 'Quiz', icon: <QuizIcon fontSize="small" />, color: '#F59E0B' },
  { key: 'project', label: 'Project', icon: <ProjectIcon fontSize="small" />, color: '#10B981' },
  { key: 'other', label: 'Other', icon: <OtherIcon fontSize="small" />, color: '#6B7280' },
];

export const getAssignmentTypeInfo = (type: AssignmentType) => {
  return ASSIGNMENT_TYPES.find((t) => t.key === type) || ASSIGNMENT_TYPES[4];
};

interface ClassData {
  name: string;
  subject?: string;
  schoolId?: string;
  teacher?: string;
  room?: string;
  period?: string;
  gradeLevel?: string;
  startDate?: string;
  endDate?: string;
  days?: string[];
  startTime?: string;
  endTime?: string;
  schedule?: string;
  notes?: string;
  memberIds?: string[];
  assignments?: ClassAssignment[];
  fileIds?: string[];
}

interface ClassFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ClassData, familyIds: string[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  classItem?: { id: string; data?: { data?: ClassData } } | null;
  schools?: { id: string; data?: { data?: { name: string } } }[];
}

export const ClassFormDialog: React.FC<ClassFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  classItem,
  schools = [],
}) => {
  const { memberList, getMemberName, getMemberColor } = useFamily();
  const isEditing = !!classItem;
  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection();

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [schoolId, setSchoolId] = useState('');
  const [teacher, setTeacher] = useState('');
  const [room, setRoom] = useState('');
  const [period, setPeriod] = useState('');
  const [days, setDays] = useState<string[]>([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [assignments, setAssignments] = useState<ClassAssignment[]>([]);
  const [fileIds, setFileIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Assignment form state
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [editingAssignmentId, setEditingAssignmentId] = useState<string | null>(null);
  const [assignmentType, setAssignmentType] = useState<AssignmentType>('homework');
  const [assignmentName, setAssignmentName] = useState('');
  const [assignmentDueDate, setAssignmentDueDate] = useState('');
  const [assignmentDescription, setAssignmentDescription] = useState('');

  const resetAssignmentForm = () => {
    setShowAssignmentForm(false);
    setEditingAssignmentId(null);
    setAssignmentType('homework');
    setAssignmentName('');
    setAssignmentDueDate('');
    setAssignmentDescription('');
  };

  const handleAddAssignment = () => {
    if (!assignmentName.trim()) return;
    const newAssignment: ClassAssignment = {
      id: editingAssignmentId || crypto.randomUUID(),
      type: assignmentType,
      name: assignmentName.trim(),
      dueDate: assignmentDueDate || undefined,
      description: assignmentDescription.trim() || undefined,
      completed: editingAssignmentId
        ? assignments.find((a) => a.id === editingAssignmentId)?.completed || false
        : false,
    };
    if (editingAssignmentId) {
      setAssignments((prev) => prev.map((a) => (a.id === editingAssignmentId ? newAssignment : a)));
    } else {
      setAssignments((prev) => [...prev, newAssignment]);
    }
    resetAssignmentForm();
  };

  const handleEditAssignment = (assignment: ClassAssignment) => {
    setEditingAssignmentId(assignment.id);
    setAssignmentType(assignment.type);
    setAssignmentName(assignment.name);
    setAssignmentDueDate(assignment.dueDate || '');
    setAssignmentDescription(assignment.description || '');
    setShowAssignmentForm(true);
  };

  const handleDeleteAssignment = (id: string) => {
    setAssignments((prev) => prev.filter((a) => a.id !== id));
    if (editingAssignmentId === id) resetAssignmentForm();
  };

  const handleToggleAssignment = (id: string) => {
    setAssignments((prev) =>
      prev.map((a) => (a.id === id ? { ...a, completed: !a.completed } : a))
    );
  };

  useEffect(() => {
    if (open) {
      if (classItem) {
        const data = (classItem.data?.data || {}) as any;
        setName(data.name || data.class_name || '');
        setSubject(data.subject || '');
        setSchoolId(data.schoolId || '');
        setTeacher(data.teacher || '');
        setRoom(data.room || '');
        setPeriod(data.period || '');
        setDays(data.days || []);
        setStartTime(data.startTime || data.start_time || '');
        setEndTime(data.endTime || data.end_time || '');
        setGradeLevel(data.gradeLevel || data.grade_level || '');
        setStartDate(data.startDate || data.start_date || '');
        setEndDate(data.endDate || data.end_date || '');
        setNotes(data.notes || '');
        setMemberIds(data.memberIds || []);
        setAssignments(data.assignments || []);
        setFileIds(data.fileIds || []);
      } else {
        setName('');
        setSubject('');
        setSchoolId('');
        setTeacher('');
        setRoom('');
        setPeriod('');
        setDays([]);
        setStartTime('');
        setEndTime('');
        setGradeLevel('');
        setStartDate('');
        setEndDate('');
        setNotes('');
        setMemberIds([]);
        setAssignments([]);
        setFileIds([]);
      }
      setError(null);
      resetAssignmentForm();
    }
  }, [open, classItem]);

  const toggleDay = (day: string) => {
    setDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  // Compute schedule string from structured fields
  const computedSchedule = (() => {
    const parts: string[] = [];
    if (days.length > 0) parts.push(days.join('/'));
    if (startTime) {
      let time = startTime;
      if (endTime) time += ` - ${endTime}`;
      parts.push(time);
    }
    return parts.join(' ') || undefined;
  })();

  const handleSave = async () => {
    if (!name.trim() && !subject) {
      setError('Class name or subject is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        name: name.trim() || subject,
        subject: subject || undefined,
        schoolId: schoolId || undefined,
        teacher: teacher.trim() || undefined,
        room: room.trim() || undefined,
        period: period.trim() || undefined,
        gradeLevel: gradeLevel || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        days: days.length > 0 ? days : undefined,
        startTime: startTime || undefined,
        endTime: endTime || undefined,
        schedule: computedSchedule,
        notes: notes.trim() || undefined,
        memberIds: memberIds.length > 0 ? memberIds : undefined,
        assignments: assignments.length > 0 ? assignments : undefined,
        fileIds: fileIds.length > 0 ? fileIds : undefined,
      }, selectedFamilyIds);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save class');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete class');
    } finally {
      setSaving(false);
    }
  };

  const toggleMember = (memberId: string) => {
    setMemberIds((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Class' : 'New Class'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          {/* Subject */}
          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>Subject</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {SUBJECT_OPTIONS.map((s) => (
                <Chip
                  key={s.key}
                  label={s.key}
                  onClick={() => setSubject(subject === s.key ? '' : s.key)}
                  variant={subject === s.key ? 'filled' : 'outlined'}
                  sx={{
                    cursor: 'pointer',
                    ...(subject === s.key && { bgcolor: s.color, color: '#fff', '&:hover': { bgcolor: s.color } }),
                  }}
                />
              ))}
            </Box>
          </Grid>

          <Grid size={12}>
            <TextField label="Class Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth autoFocus placeholder="e.g., Algebra II, World History" />
          </Grid>

          {showFamilySelector && !isEditing && (
            <Grid size={12}>
              <FamilySelector value={selectedFamilyIds} onChange={setSelectedFamilyIds} />
            </Grid>
          )}

          {schools.length > 0 && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>School</InputLabel>
                <Select value={schoolId} label="School" onChange={(e) => setSchoolId(e.target.value)}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  {schools.map((school) => (
                    <MenuItem key={school.id} value={school.id}>
                      {school.data?.data?.name || 'Unnamed School'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="Teacher" value={teacher} onChange={(e) => setTeacher(e.target.value)} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="Room" value={room} onChange={(e) => setRoom(e.target.value)} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="Period" value={period} onChange={(e) => setPeriod(e.target.value)} fullWidth placeholder="e.g., 1st, 2nd" />
          </Grid>

          {/* Grade Level & Dates */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Grade Level</InputLabel>
              <Select value={gradeLevel} label="Grade Level" onChange={(e) => setGradeLevel(e.target.value)}>
                <MenuItem value=""><em>None</em></MenuItem>
                {['Pre-K', 'K', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th', '11th', '12th', 'College'].map((g) => (
                  <MenuItem key={g} value={g}>{g}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="End Date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>

          {/* Schedule Days */}
          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>Schedule Days</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {DAYS_OF_WEEK.map((day) => (
                <Chip
                  key={day.key}
                  label={day.label}
                  onClick={() => toggleDay(day.key)}
                  variant={days.includes(day.key) ? 'filled' : 'outlined'}
                  color={days.includes(day.key) ? 'primary' : 'default'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Grid>

          {/* Times */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Start Time" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="End Time" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>Students</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {memberList.map((member) => {
                const isSelected = memberIds.includes(member.id);
                const memberName = getMemberName(member.id);
                const color = getMemberColor(member.id);
                return (
                  <Chip
                    key={member.id}
                    avatar={<Avatar sx={{ bgcolor: color }}>{memberName.charAt(0)}</Avatar>}
                    label={memberName}
                    onClick={() => toggleMember(member.id)}
                    variant={isSelected ? 'filled' : 'outlined'}
                    color={isSelected ? 'primary' : 'default'}
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
            </Box>
          </Grid>

          {/* Attachments */}
          <Grid size={12}>
            <FileAttachments
              fileIds={fileIds}
              onChange={setFileIds}
              onPersist={classItem ? async (ids) => {
                const data = classItem.data?.data || {};
                await updateData(classItem.id, 'class', { ...data, fileIds: ids });
              } : undefined}
              dataId={classItem?.id}
              canUpload={isEditing}
            />
          </Grid>

          {/* Assignments */}
          <Grid size={12}>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Assignments</Typography>
              {!showAssignmentForm && (
                <Button size="small" startIcon={<AddIcon />} onClick={() => { resetAssignmentForm(); setShowAssignmentForm(true); }}>
                  Add
                </Button>
              )}
            </Box>

            {assignments.length > 0 && (
              <List dense disablePadding sx={{ mb: 1 }}>
                {assignments.map((assignment) => {
                  const typeInfo = getAssignmentTypeInfo(assignment.type);
                  const isOverdue = assignment.dueDate && !assignment.completed && new Date(assignment.dueDate + 'T23:59:59') < new Date();
                  return (
                    <ListItem
                      key={assignment.id}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 0.5,
                        py: 0.5,
                        opacity: assignment.completed ? 0.5 : 1,
                      }}
                      secondaryAction={
                        <Box>
                          <IconButton size="small" onClick={() => handleEditAssignment(assignment)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteAssignment(assignment.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Checkbox
                          edge="start"
                          size="small"
                          checked={assignment.completed || false}
                          onChange={() => handleToggleAssignment(assignment.id)}
                        />
                      </ListItemIcon>
                      <ListItemIcon sx={{ minWidth: 28, color: typeInfo.color }}>
                        {typeInfo.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{ textDecoration: assignment.completed ? 'line-through' : 'none' }}
                          >
                            {assignment.name}
                          </Typography>
                        }
                        secondary={
                          assignment.dueDate ? (
                            <Typography
                              variant="caption"
                              sx={{ color: isOverdue ? 'error.main' : 'text.secondary', fontWeight: isOverdue ? 600 : 400 }}
                            >
                              Due: {new Date(assignment.dueDate + 'T00:00:00').toLocaleDateString()}
                              {isOverdue ? ' (overdue)' : ''}
                            </Typography>
                          ) : undefined
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}

            {showAssignmentForm && (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 1, bgcolor: 'action.hover' }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {editingAssignmentId ? 'Edit Assignment' : 'New Assignment'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                  {ASSIGNMENT_TYPES.map((t) => (
                    <Chip
                      key={t.key}
                      label={t.label}
                      size="small"
                      icon={t.icon}
                      onClick={() => setAssignmentType(t.key)}
                      variant={assignmentType === t.key ? 'filled' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        ...(assignmentType === t.key && { bgcolor: t.color, color: '#fff', '& .MuiChip-icon': { color: '#fff' }, '&:hover': { bgcolor: t.color } }),
                      }}
                    />
                  ))}
                </Box>
                <TextField
                  label="Assignment Name"
                  value={assignmentName}
                  onChange={(e) => setAssignmentName(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mb: 1.5 }}
                  autoFocus
                />
                <TextField
                  label="Due Date"
                  type="date"
                  value={assignmentDueDate}
                  onChange={(e) => setAssignmentDueDate(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  label="Description (optional)"
                  value={assignmentDescription}
                  onChange={(e) => setAssignmentDescription(e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  sx={{ mb: 1.5 }}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={resetAssignmentForm}>Cancel</Button>
                  <Button size="small" variant="contained" onClick={handleAddAssignment} disabled={!assignmentName.trim()}>
                    {editingAssignmentId ? 'Update' : 'Add'}
                  </Button>
                </Box>
              </Box>
            )}

            {assignments.length === 0 && !showAssignmentForm && (
              <Typography variant="body2" color="text.secondary">No assignments yet</Typography>
            )}
          </Grid>

          <Grid size={12}>
            <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={saving} sx={{ mr: 'auto' }}>Delete</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default {
  SchoolFormDialog,
  ClassFormDialog,
};
